/**
 * What a character's vertical progress is, as one record, and every operation on it.
 *
 * Two fields, and neither of them is derivable from the other. Everything else a screen wants is
 * computed here rather than stored: the level comes from the experience, the perk points spent come
 * from the ranks, and the stat points spent come from the stat block itself. Nothing that can be
 * recomputed is persisted, which is one fewer field a save can contradict and the reason a respec
 * is a reset rather than a refund.
 *
 * The stat block is not in here. Stats already live on the profile and already have a contract that
 * clamps them, so progression takes one as a parameter where it needs one rather than keeping a
 * second copy that could drift from the first.
 *
 * Every operation returns a result and nothing throws. A perk id that no longer exists is refused,
 * a rank count out of range is refused, and an experience total out of range is clamped: ids are
 * refused, magnitudes are clamped, the same rule the save module states in its own words.
 */

import { MAX_STAT, STAT_ORDER, statValue, statsFrom } from '@/sim/combat/statContract';
import { PERK_ORDER, isPerkId, perk } from '@/sim/progression/perkRegistry';
import { clampXp, levelForXp } from '@/sim/progression/experienceCurve';
import { fail, succeed } from '@/sim/progression/progressionResult';
import { perkPointsFor, spentStatPoints, statPointsFor } from '@/sim/progression/progressionPoints';
import type { PerkId } from '@/sim/progression/perkContract';
import type { ProgressionResult } from '@/sim/progression/progressionResult';
import type { StatBlock, StatId } from '@/sim/combat/statContract';

/** Ranks taken, by perk. Partial, so a perk added later needs no migration to be absent. */
export type PerkRanks = Readonly<Partial<Record<PerkId, number>>>;

export interface ProgressState {
  /** Total earned over the character's life, never spent and never reduced. */
  readonly xp: number;
  readonly perkRanks: PerkRanks;
}

/** A character who has done nothing yet. */
export const NEW_PROGRESS: ProgressState = { xp: 0, perkRanks: {} };

export function progressLevel(progress: ProgressState): number {
  return levelForXp(progress.xp);
}

/**
 * Experience in. Saturates at the cap rather than accumulating past it: a number nothing reads is
 * a field that lies, and a bar that is full and stays full reads as finished.
 */
export function awardXp(progress: ProgressState, amount: number): ProgressState {
  if (!Number.isFinite(amount) || amount <= 0) return progress;

  const xp = clampXp(progress.xp + Math.floor(amount));

  return xp === progress.xp ? progress : { xp, perkRanks: progress.perkRanks };
}

export function rankOf(progress: ProgressState, id: PerkId): number {
  return progress.perkRanks[id] ?? 0;
}

/** Perk points already spent, summed in `PERK_ORDER` so two peers agree on the total. */
export function spentPerkPoints(progress: ProgressState): number {
  let spent = 0;

  for (const id of PERK_ORDER) spent += rankOf(progress, id);

  return spent;
}

export function unspentPerkPoints(progress: ProgressState): number {
  return Math.max(0, perkPointsFor(progressLevel(progress)) - spentPerkPoints(progress));
}

export function unspentStatPoints(progress: ProgressState, stats: StatBlock): number {
  return Math.max(0, statPointsFor(progressLevel(progress)) - spentStatPoints(stats));
}

/**
 * Whether the next rank of a perk could be taken, and why not when it could not. The upgrade screen
 * calls this to grey a node out, and `takePerk` calls it to decide, so the reason on the tooltip
 * and the reason for the refusal are the same sentence by construction.
 */
export function perkAvailable(
  progress: ProgressState,
  id: PerkId,
): ProgressionResult<{ readonly nextRank: number }> {
  const def = perk(id);
  const rank = rankOf(progress, id);

  if (rank >= def.maxRanks) {
    return fail({ reason: 'atMaxRank', perkId: id, maxRanks: def.maxRanks });
  }

  if (progressLevel(progress) < def.minLevel) {
    return fail({ reason: 'levelTooLow', perkId: id, minLevel: def.minLevel });
  }

  for (const requirement of def.requires) {
    if (rankOf(progress, requirement) < perk(requirement).maxRanks) {
      return fail({ reason: 'missingRequirement', perkId: id, requires: requirement });
    }
  }

  const available = unspentPerkPoints(progress);

  if (available < 1) return fail({ reason: 'noPerkPoints', available });

  return succeed({ nextRank: rank + 1 });
}

/** One perk point into one perk. */
export function takePerk(progress: ProgressState, id: PerkId): ProgressionResult<ProgressState> {
  const check = perkAvailable(progress, id);

  if (!check.ok) return fail(check.failure);

  return succeed({
    xp: progress.xp,
    perkRanks: { ...progress.perkRanks, [id]: check.value.nextRank },
  });
}

/**
 * One stat point into one stat. Returns the new block rather than a progress record, because the
 * spend is recorded in the block itself and there is nothing in `ProgressState` to change.
 */
export function spendStatPoint(
  progress: ProgressState,
  stats: StatBlock,
  id: StatId,
): ProgressionResult<StatBlock> {
  if (statValue(stats, id) >= MAX_STAT) {
    return fail({ reason: 'atStatCap', statId: id, cap: MAX_STAT });
  }

  const available = unspentStatPoints(progress, stats);

  if (available < 1) return fail({ reason: 'noStatPoints', available });

  return succeed(statsFrom({ ...stats, [id]: statValue(stats, id) + 1 }));
}

/**
 * Everything back. Experience is untouched, which is the whole design: a respec re-spends what a
 * character earned rather than costing them what they earned. Stats go back to baseline through
 * `statsFrom`, so the caller gets a block it can write straight to the profile.
 */
export function respec(progress: ProgressState): {
  readonly progress: ProgressState;
  readonly stats: StatBlock;
} {
  return { progress: { xp: progress.xp, perkRanks: {} }, stats: statsFrom({}) };
}

/**
 * A record from outside, checked. The gate a save goes through, and the reason `readProfile` needs
 * no progression rules of its own.
 *
 * Ids are refused and magnitudes are clamped, the same rule the save module states in its own
 * words. A perk this build has never heard of is refused rather than dropped, because silently
 * losing a perk somebody spent a level on is the same unrecoverable, unexplainable loss as gear
 * disappearing from a backpack. Experience is clamped, because retuning the curve should not cost
 * anyone their character.
 *
 * A record spending more points than its level granted is deliberately not refused. It is the
 * shape a save takes the moment the point grants are retuned downward, and refusing it would brick
 * every existing character over a balance change. `unspentPerkPoints` floors at zero, so such a
 * character simply has nothing to spend until the curve catches up. Nothing here is signed and
 * there is no server, so this gate is for safety rather than for trust, and an edited save buying
 * itself a perk is accepted the same way an edited save always has been.
 */
export function validateProgress(
  xp: number,
  ranks: Readonly<Record<string, number>>,
): ProgressionResult<ProgressState> {
  const perkRanks: Partial<Record<PerkId, number>> = {};

  // Sorted so a malformed record is refused at the same key on every machine, which is what makes
  // the failure a test can assert on rather than whichever key the engine happened to enumerate.
  for (const key of Object.keys(ranks).sort()) {
    if (!isPerkId(key)) return fail({ reason: 'unknownPerk', perkId: key });

    const rank = ranks[key] ?? 0;

    if (!Number.isInteger(rank) || rank < 0 || rank > perk(key).maxRanks) {
      return fail({ reason: 'invalidRank', perkId: key, rank });
    }

    if (rank > 0) perkRanks[key] = rank;
  }

  return succeed({ xp: clampXp(xp), perkRanks });
}

/** Every stat, in `STAT_ORDER`, that could still take a point. What the screen offers. */
export function spendableStats(progress: ProgressState, stats: StatBlock): readonly StatId[] {
  if (unspentStatPoints(progress, stats) < 1) return [];

  return STAT_ORDER.filter((id) => statValue(stats, id) < MAX_STAT);
}
